/* eslint-disable react/forbid-elements */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect } from 'react';
import { Measurement } from '../../../../../common/src/domain/drone/measurement/measurementTableSchema';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { useTranslate } from '../../../mixins/translate/translate';
import { fetchGet, fetchPost } from '../../../store/fetch';
import * as htmlHelper from '../../../utils/htmlHelper';
import { Modal } from '../../common/Modal/Modal';
import {
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelInput,
    EditPanelRow,
    EditPanelSwitch,
    LimitTableWidth,
    PanelButtonLink,
    PanelLink,
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
} from '../../elements';
import { Spinner } from '../../spinners';
import { Component } from '../../utils/Component';
import { convertToLength, IWaveformChartProps, WaveformChart, WaveformData } from '../Common/WaveformChart';

interface IEditWaveformData {
    isEditing: boolean;
    editingFirstPeak: boolean;
    firstPeak?: number | undefined;
    secondPeak?: number | undefined;
    length?: number | undefined;
    useGate2: boolean;
}

interface IProject {
    id: string;
    name: string;
    isScanLineOnXAxis: boolean;
    maxScanLines?: number | undefined;
    maxScanLinePositions?: number | undefined;
    scanLineLabel?: string | undefined;
    positionLabel?: string | undefined;
}

interface IChartModalProps {
    modalId: string;
    onClose: () => void;
    cell: IValueCell;
    project: IProject;
    nominalValues: number[];
    showResidualLife: boolean;
    constructionYear?: number | undefined;
    position: number;
    isEditMode: boolean;
    handleLengthChanged: (value: number) => void;
}

function ChartModal(props: IChartModalProps) {
    const [editWaveformData, setEditWaveformData] = React.useState<IEditWaveformData>({ isEditing: false, editingFirstPeak: true, useGate2: false });

    const [measurement, setMeasurement] = React.useState<{ chartData: IWaveformChartProps } | null>(null);

    useEffect(() => {
        async function load() {
            const measurementResponse = await fetchGet(`/api/utprojects/${props.project.id}/measurements/${props.cell.id}`);
            setMeasurement(measurementResponse.json);
        }
        void load();
    }, [props.cell.id, props.project.id]);

    function calculateLength(firstPeak?: number, secondPeak?: number) {
        if (firstPeak === undefined) {
            return undefined;
        }
        if (secondPeak === undefined) {
            return firstPeak;
        }
        return secondPeak - firstPeak;
    }

    function handleWaveformSelectPoint(data?: { length: number }) {
        if (editWaveformData.isEditing && data) {
            if (editWaveformData.editingFirstPeak) {
                const editingFirstPeak = editWaveformData.useGate2 ? false : true; // Only switch to second peak if gate 2 was used
                setEditWaveformData((value) => ({
                    ...value,
                    firstPeak: data.length,
                    editingFirstPeak,
                    length: calculateLength(data.length, value.secondPeak),
                }));
            } else {
                setEditWaveformData((value) => ({
                    ...value,
                    secondPeak: data.length,
                    editingFirstPeak: true,
                    length: calculateLength(value.firstPeak, data.length),
                }));
            }
        }
    }

    function startMeasureNewLength(chartData: IWaveformChartProps) {
        const { waveFormResult } = chartData;
        let firstPeak: number | undefined = undefined;
        let secondPeak: number | undefined = undefined;

        if (waveFormResult.gate1Amp > 0.01) {
            const gate1MicroSeconds = waveFormResult.gate1Tof - waveFormResult.adDelay;
            firstPeak = convertToLength(gate1MicroSeconds, chartData.velocity, chartData.unitOfLength);
        }

        if (waveFormResult.useGate2 && waveFormResult.gate2Amp > 0.01) {
            const gate2MicroSeconds = waveFormResult.gate2Tof - waveFormResult.adDelay;
            secondPeak = convertToLength(gate2MicroSeconds, chartData.velocity, chartData.unitOfLength);
        }

        const length = calculateLength(firstPeak, secondPeak);

        setEditWaveformData({ isEditing: true, firstPeak, secondPeak, editingFirstPeak: true, length, useGate2: waveFormResult.useGate2 });
    }

    function saveNewMeasuredLength() {
        if (editWaveformData.length) {
            props.handleLengthChanged(editWaveformData.length);
        }
        stopMeasureNewLength();
        props.onClose();
    }

    function stopMeasureNewLength() {
        setEditWaveformData((value) => ({ ...value, isEditing: false }));
    }

    if (!measurement) {
        return null;
    }

    return (
        <Modal id={props.modalId} classNames="wavechart" closeOnBackdropClick={true} onClose={props.onClose}>
            <Modal.Title>
                <h2>
                    <DisplayValue
                        cell={props.cell}
                        unitOfLength={UnitOfLength.Mm}
                        nominalValues={props.project.isScanLineOnXAxis ? props.nominalValues : []}
                        position={props.position}
                        showResidualLife={props.showResidualLife}
                        constructionYear={props.constructionYear}
                    />
                </h2>
            </Modal.Title>
            <Modal.Content style={{ minHeight: '430px', width: '750px' }}>
                <WaveformChart {...measurement.chartData} onSelectPoint={handleWaveformSelectPoint} />
            </Modal.Content>
            <Modal.Status>
                {(editWaveformData.isEditing && (
                    <>
                        <EditPanelRow>
                            <EditPanelCell rowCount={2}>
                                <strong>First peak</strong>
                            </EditPanelCell>
                            <EditPanelCell rowCount={2}>{editWaveformData.firstPeak?.toFixed(2)}</EditPanelCell>
                        </EditPanelRow>
                        <EditPanelRow>
                            <EditPanelCell rowCount={2}>
                                <strong>Second peak</strong>
                            </EditPanelCell>
                            {editWaveformData.useGate2 && <EditPanelCell rowCount={4}>{editWaveformData.secondPeak?.toFixed(2)}</EditPanelCell>}
                        </EditPanelRow>
                        <EditPanelRow>
                            <EditPanelCell rowCount={2}>
                                <strong>Length</strong>
                            </EditPanelCell>
                            <EditPanelCell rowCount={2}>{editWaveformData.length?.toFixed(2)}</EditPanelCell>
                        </EditPanelRow>
                    </>
                )) || <WaveformData {...measurement.chartData} />}

                {props.isEditMode && (
                    <div className="text-right">
                        {(editWaveformData.isEditing && (
                            <>
                                <button type="button" className="btn btn-default btn-sm mr10" onClick={stopMeasureNewLength}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-default btn-primary btn-sm mr10" onClick={() => saveNewMeasuredLength()}>
                                    Save & Close
                                </button>
                            </>
                        )) || (
                            <button
                                type="button"
                                className="btn btn-default btn-primary btn-sm mr10"
                                onClick={() => startMeasureNewLength(measurement.chartData)}
                            >
                                Select peaks
                            </button>
                        )}
                    </div>
                )}
            </Modal.Status>
        </Modal>
    );
}

function FreeMeasurementsOverview(props: {
    measurements: MeasurementView[];
    name: string;
    isEditMode: boolean;
    project: IProject;
    onValueChange(
        scanLineNumber: number,
        positionNumber: number,
        pointNumber: number,
        isDeleteGraph: boolean,
        value?: number,
        id?: string,
    ): Promise<void>;
}) {
    const [showChartMeasurement, setShowChartMeasurement] = React.useState<undefined | { index?: number | undefined }>(undefined);

    if (props.measurements.length === 0) {
        return null;
    }
    const t = useTranslate();
    const [editingValueId, setEditingValueId] = React.useState<undefined | string>(undefined);

    async function handleValueChanged(isDeleteGraph: boolean, measurement: { id: string; positionNumber: number }, value?: number) {
        await props.onValueChange(1, measurement.positionNumber, 1, isDeleteGraph, value, measurement.id);
        setEditingValueId(undefined);
    }

    return (
        <LimitTableWidth rows={1}>
            <Table whiteBackground>
                <TableHead>
                    <TableHeadRow>
                        <TableHeader>{t('project.measurement')}</TableHeader>
                        <TableHeader>{t('project.value')}</TableHeader>
                    </TableHeadRow>
                </TableHead>
                <TableBody>
                    {props.measurements.map((measurement, index) => (
                        <tr key={measurement.id}>
                            <td>{index + 1}</td>
                            <td className="fs12">
                                {editingValueId === measurement.id && (
                                    <EditValue
                                        cell={{
                                            id: measurement.id,
                                            value: measurement.value,
                                            createdOnDateTime: measurement.createdOnDateTime,
                                            hasChartData: measurement.hasChartData,
                                        }}
                                        onChange={async (isDeleteGraph, value) => handleValueChanged(isDeleteGraph, measurement, value)}
                                        onCancel={() => setEditingValueId(undefined)}
                                        initialValue={
                                            measurement.value === undefined
                                                ? undefined
                                                : UnitOfLength.Mm === UnitOfLength.Mm
                                                  ? measurement.value.toFixed(2)
                                                  : (measurement.value / 25.4).toFixed(3)
                                        }
                                    />
                                )}

                                <span
                                    onClick={(e: React.SyntheticEvent) => {
                                        if (props.isEditMode) {
                                            setEditingValueId(measurement.id);
                                        }
                                        e.stopPropagation();
                                    }}
                                >
                                    <DisplayValue
                                        cell={{
                                            id: measurement.id,
                                            value: measurement.value,
                                            createdOnDateTime: measurement.createdOnDateTime,
                                            hasChartData: measurement.hasChartData,
                                        }}
                                        unitOfLength={UnitOfLength.Mm}
                                    />
                                </span>

                                {measurement.hasChartData && (
                                    <>
                                        <i
                                            className="ml5 fa fa-bar-chart-o pointer"
                                            onClick={(e: React.SyntheticEvent) => {
                                                if (showChartMeasurement?.index === index) {
                                                    setShowChartMeasurement({ index: undefined });
                                                } else {
                                                    setEditingValueId(undefined);
                                                    setShowChartMeasurement({ index });
                                                }
                                                e.stopPropagation();
                                            }}
                                        />
                                        {showChartMeasurement?.index === index && (
                                            <ChartModal
                                                modalId={measurement.id}
                                                cell={{
                                                    id: measurement.id,
                                                    value: measurement.value,
                                                    createdOnDateTime: measurement.createdOnDateTime,
                                                    hasChartData: measurement.hasChartData,
                                                }}
                                                handleLengthChanged={(value) => void handleValueChanged(false, measurement, value)}
                                                nominalValues={[0]}
                                                position={measurement.positionNumber}
                                                showResidualLife={false}
                                                constructionYear={0}
                                                isEditMode={props.isEditMode}
                                                project={props.project}
                                                onClose={() => {
                                                    setShowChartMeasurement({ index: undefined });
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </td>
                        </tr>
                    ))}
                </TableBody>
            </Table>
        </LimitTableWidth>
    );
}

function createEmptyGridForScanLines(maxPositionNumber: number, maxScanlineNumber: number) {
    const rows: (IValueCell | undefined)[][] = [];
    for (let i = 0; i < maxPositionNumber; i++) {
        const row: (IValueCell | undefined)[] = [];
        for (let j = 0; j < maxScanlineNumber; j++) {
            row.push(undefined);
        }
        rows.push(row);
    }

    return rows;
}

function DisplayNominalValue(nominalArray: number[], position: number) {
    if (nominalArray && position in nominalArray) {
        return <span className="fs12 fw200">&nbsp;({nominalArray[position]}&nbsp;mm)</span>;
    }
    return null;
}

function colorCode(nominalArray?: number[], position?: number, value?: number) {
    if (nominalArray && position !== undefined && position in nominalArray && value !== undefined) {
        const nominalValue = nominalArray[position]!;
        const rejectionValue = 0.75; // The lower bound index. For example, 0.5 represents a 50% decrease as the lower limit.
        const darkGreen = [0, 100, 0];
        const red = [255, 0, 0];

        if (!isNaN(nominalValue)) {
            let weight = (value / nominalValue - rejectionValue) / (1 - rejectionValue);
            if (weight < 0) {
                weight = 0;
            } else if (weight > 1) {
                weight = 1;
            }
            const rgb = `rgb(${Math.round(darkGreen[0]! * weight + red[0]! * (1 - weight))}, ${Math.round(
                darkGreen[1]! * weight + red[1]! * (1 - weight),
            )}, ${Math.round(darkGreen[2]! * weight + red[2]! * (1 - weight))})`;
            return rgb;
        }
    }
    return '(0,0,0)';
}

function ScanLineMeasurementsOverview(props: {
    measurements: MeasurementView[];
    nominalValues: number[];
    isEditMode: boolean;
    showResidualLife: boolean;
    constructionYear?: number | undefined;
    project: IProject;
    onValueChange(
        scanLineNumber: number,
        positionNumber: number,
        pointNumber: number,
        isDeleteGraph: boolean,
        value?: number,
        id?: string,
    ): Promise<void>;
}) {
    if (props.measurements.length === 0) {
        return null;
    }
    const t = useTranslate();

    const [editingValueId, setEditingValueId] = React.useState<undefined | { columnIndex: number; rowIndex: number }>(undefined);
    const [showChartMeasurement, setShowChartMeasurement] = React.useState<
        undefined | { columnIndex?: number | undefined; rowIndex?: number | undefined }
    >(undefined);

    const maxScanlineNumber =
        props.project.maxScanLines! < 1 ? Math.max(...props.measurements.map((m) => m.scanLineNumber)) : props.project.maxScanLines;
    const maxPositionNumber =
        props.project.maxScanLinePositions! < 1 ? Math.max(...props.measurements.map((m) => m.positionNumber)) : props.project.maxScanLinePositions;
    const scanLineLabel = props.project.scanLineLabel ?? t('project.scanLines');
    const positionLabel = props.project.positionLabel ?? t('project.position');

    async function handleValueChanged(columnIndex: number, rowIndex: number, isDeleteGraph: boolean, value?: number, id?: string) {
        const scanLineNumber = props.project.isScanLineOnXAxis ? columnIndex + 1 : rowIndex + 1;
        const positionNumber = props.project.isScanLineOnXAxis ? maxPositionNumber! - rowIndex : columnIndex + 1;
        await props.onValueChange(scanLineNumber, positionNumber, 1, isDeleteGraph, value, id);
        setEditingValueId(undefined);
    }

    let grid: (IValueCell | undefined)[][] | undefined;

    try {
        if (props.project.isScanLineOnXAxis) {
            grid = createEmptyGridForScanLines(maxPositionNumber!, maxScanlineNumber!);
            for (const measurement of props.measurements) {
                grid[measurement.positionNumber - 1]![measurement.scanLineNumber - 1] = {
                    id: measurement.id,
                    value: measurement.value,
                    createdOnDateTime: measurement.createdOnDateTime,
                    hasChartData: measurement.hasChartData,
                };
            }
            grid.reverse();
        } else {
            grid = createEmptyGridForScanLines(maxScanlineNumber!, maxPositionNumber!);
            for (const measurement of props.measurements) {
                grid[measurement.scanLineNumber - 1]![measurement.positionNumber - 1] = {
                    id: measurement.id,
                    value: measurement.value,
                    createdOnDateTime: measurement.createdOnDateTime,
                    hasChartData: measurement.hasChartData,
                };
            }
        }
    } catch (e) {
        console.log('Could not create grid. Error: ', e);
    }

    if (grid === undefined || grid[0] === undefined) {
        return null;
    }

    const gridLength = grid.length;

    return (
        <LimitTableWidth rows={grid[0].length}>
            <Table whiteBackground>
                <TableHead>
                    <TableHeadRow>
                        <th> </th>
                        <th colSpan={grid[0].length}>{props.project.isScanLineOnXAxis ? scanLineLabel : positionLabel}</th>
                    </TableHeadRow>
                    <TableRow>
                        <th>{props.project.isScanLineOnXAxis ? positionLabel : scanLineLabel}</th>
                        {grid[0].map((_i, index) => (
                            <th key={index}>{index + 1}</th>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {grid.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            <td>
                                {props.project.isScanLineOnXAxis ? gridLength - rowIndex : rowIndex + 1}
                                {props.project.isScanLineOnXAxis && DisplayNominalValue(props.nominalValues, gridLength - rowIndex - 1)}
                            </td>
                            {row.map((cell, columnIndex) => (
                                <td key={columnIndex} className="fs12">
                                    {editingValueId && editingValueId.columnIndex === columnIndex && editingValueId.rowIndex === rowIndex && (
                                        <EditValue
                                            cell={cell}
                                            onChange={async (isDeleteGraph, value) =>
                                                handleValueChanged(columnIndex, rowIndex, isDeleteGraph, value, cell?.id ? cell.id : undefined)
                                            }
                                            onCancel={() => setEditingValueId(undefined)}
                                            initialValue={
                                                cell === undefined
                                                    ? undefined
                                                    : UnitOfLength.Mm === UnitOfLength.Mm
                                                      ? cell.value.toFixed(2)
                                                      : (cell.value / 25.4).toFixed(3)
                                            }
                                        />
                                    )}
                                    <span
                                        onClick={(e: React.SyntheticEvent) => {
                                            e.stopPropagation();
                                            if (props.isEditMode) {
                                                setEditingValueId({ columnIndex, rowIndex });
                                            }
                                        }}
                                    >
                                        <DisplayValue
                                            cell={cell}
                                            unitOfLength={UnitOfLength.Mm}
                                            nominalValues={props.project.isScanLineOnXAxis ? props.nominalValues : []}
                                            position={props.project.isScanLineOnXAxis ? gridLength - rowIndex - 1 : rowIndex}
                                            showResidualLife={props.showResidualLife}
                                            constructionYear={props.constructionYear}
                                        />
                                    </span>

                                    {cell?.hasChartData && (
                                        <>
                                            <i
                                                className="ml5 fa fa-bar-chart-o pointer"
                                                onClick={(e: React.SyntheticEvent) => {
                                                    if (
                                                        showChartMeasurement?.columnIndex === columnIndex &&
                                                        showChartMeasurement?.rowIndex === rowIndex
                                                    ) {
                                                        setShowChartMeasurement({ columnIndex: undefined, rowIndex: undefined });
                                                    } else {
                                                        setEditingValueId(undefined);
                                                        setShowChartMeasurement({ columnIndex, rowIndex });
                                                    }
                                                    e.stopPropagation();
                                                }}
                                            />
                                            {showChartMeasurement?.columnIndex === columnIndex && showChartMeasurement?.rowIndex === rowIndex && (
                                                <ChartModal
                                                    modalId={`${columnIndex}-${rowIndex}`}
                                                    cell={cell}
                                                    handleLengthChanged={(value) =>
                                                        void handleValueChanged(columnIndex, rowIndex, false, value, cell?.id ? cell.id : undefined)
                                                    }
                                                    nominalValues={props.project.isScanLineOnXAxis ? props.nominalValues : []}
                                                    position={props.project.isScanLineOnXAxis ? gridLength - rowIndex - 1 : rowIndex}
                                                    showResidualLife={props.showResidualLife}
                                                    constructionYear={props.constructionYear}
                                                    project={props.project}
                                                    isEditMode={props.isEditMode}
                                                    onClose={() => {
                                                        setShowChartMeasurement({ columnIndex: undefined, rowIndex: undefined });
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </TableBody>
            </Table>
        </LimitTableWidth>
    );
}

function createEmptyGridForScanLinesWithPoints(maxPositionNumber: number, maxScanlineNumber: number, maxPointNumber: number) {
    const positions: (IValueCell | undefined)[][][] = [];

    for (let positionIndex = 0; positionIndex < maxPositionNumber; positionIndex++) {
        const rows: (IValueCell | undefined)[][] = [];
        for (let i = 0; i < maxPointNumber; i++) {
            const row: (IValueCell | undefined)[] = [];
            for (let j = 0; j < maxScanlineNumber; j++) {
                row.push(undefined);
            }
            rows.push(row);
        }
        positions.push(rows);
    }

    return positions;
}
type MeasurementView = Pick<Measurement, 'id' | 'pointNumber' | 'scanLineNumber' | 'value' | 'positionNumber' | 'createdOnDateTime'> & {
    hasChartData: boolean;
};

// eslint-disable-next-line no-shadow
enum UnitOfLength {
    Mm = 0,
    Inch = 1,
}

interface IValueCell {
    id?: string;
    value: number;
    createdOnDateTime: string;
    hasChartData: boolean;
}

function roundToFixed(value: number, decimals: number): string {
    const factor = Math.pow(10, decimals);
    return (Math.round(value * factor) / factor).toFixed(decimals);
}

export function MeasurementValue(props: { value: number; unitOfLength: UnitOfLength; hideUnitOfLength?: boolean; colorCode: string }) {
    return (
        <>
            <span style={{ color: props.colorCode }}>
                {props.unitOfLength === UnitOfLength.Mm ? roundToFixed(props.value, 1) : `${(props.value / 25.4).toFixed(3)}"`}
            </span>
            &nbsp;
            {!props.hideUnitOfLength && <>{props.unitOfLength === UnitOfLength.Mm ? 'mm' : ''}</>}
        </>
    );
}

export function ResidualLifeValue(props: { year: string; colorCode: string }) {
    const translateText = useTranslate();

    return (
        <>
            <span style={{ color: props.colorCode }}>
                {props.year}&nbsp;{translateText('common.year')}
            </span>
        </>
    );
}

function DisplayValue(props: {
    cell?: IValueCell | undefined;
    unitOfLength: UnitOfLength;
    nominalValues?: number[] | undefined;
    position?: number | undefined;
    showResidualLife?: boolean | undefined;
    constructionYear?: number | undefined;
}) {
    if (props.cell === undefined) {
        return <span>...</span>;
    }

    if (props.showResidualLife && props.constructionYear) {
        let residualLifeYearString = '..';
        if (props.nominalValues && props.position !== undefined && props.position in props.nominalValues) {
            const yearMeasurement = parseInt(props.cell.createdOnDateTime.substr(0, 4), 10);
            const nominalValue = props.nominalValues[props.position]!;
            const rejectionValue = nominalValue * 0.5; // Rejection limit is 50% of original thickness
            const residualLifeYear =
                (props.cell.value - rejectionValue) / ((nominalValue - props.cell.value) / (yearMeasurement - props.constructionYear));
            if (props.cell.value - rejectionValue < 0) {
                residualLifeYearString = '0';
            } else if (nominalValue - props.cell.value < 0) {
                residualLifeYearString = '> 100';
            } else if (residualLifeYear > 100) {
                residualLifeYearString = '> 100';
            } else {
                residualLifeYearString = residualLifeYear.toFixed(1).toString();
            }
        }
        return <ResidualLifeValue year={residualLifeYearString} colorCode={colorCode(props.nominalValues, props.position, props.cell.value)} />;
    }
    return (
        <MeasurementValue
            value={props.cell.value}
            unitOfLength={props.unitOfLength}
            colorCode={colorCode(props.nominalValues, props.position, props.cell.value)}
        />
    );
}

// eslint-disable-next-line no-shadow
function EditValue(props: {
    cell?: IValueCell | undefined;
    initialValue?: string | undefined;
    onChange(isDeleteGraph: boolean, value?: number): Promise<void>;
    onCancel(): void;
}) {
    const [value, setValue] = React.useState<string | undefined>(undefined);
    const [isDeleteGraph, setIsDeleteGraph] = React.useState<boolean>(false);
    const translateEditValue = useTranslate();

    React.useEffect(() => {
        if (props.initialValue !== undefined) {
            setValue(props.initialValue.toString());
        }
    }, [props.initialValue]);

    async function save() {
        if (value) {
            if (isNaN(parseFloat(value))) {
                if (props.initialValue === undefined) {
                    await props.onChange(false, undefined);
                }
            } else {
                await props.onChange(isDeleteGraph, parseFloat(value));
            }
        }
    }

    return (
        <>
            <Modal closeOnBackdropClick={true} id={`${props.cell?.id}`} onClose={() => props.onCancel()}>
                <Modal.Title>
                    <h2>Edit</h2>
                </Modal.Title>
                <Modal.Content style={{ height: '100px' }}>
                    <div className="admin-form">
                        <EditPanel>
                            <EditPanelRow>
                                <EditPanelCell rowCount={4}>
                                    <EditPanelInput
                                        autoFocus
                                        name="value"
                                        restrictToNumbers={true}
                                        value={value === undefined ? '' : value}
                                        handleEnter={async () => await save()}
                                        handleChange={(newValue) => setValue(newValue)}
                                    />
                                </EditPanelCell>
                                <EditPanelCell rowCount={2}>&nbsp;</EditPanelCell>
                                <EditPanelCell rowCount={6}>
                                    <EditPanelSwitch
                                        name="replace"
                                        placeholder="Remove graph"
                                        value={isDeleteGraph}
                                        handleChange={() => setIsDeleteGraph(!isDeleteGraph)}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        </EditPanel>
                    </div>
                </Modal.Content>
                <Modal.Actions>
                    <EditPanelButtons fixWidth={true}>
                        <EditPanelButton small={true} text={translateEditValue('common.cancel')} onClick={() => props.onCancel()} />
                        <EditPanelButton small={true} text={translateEditValue('common.save')} onClick={async () => await save()} primary />
                    </EditPanelButtons>
                </Modal.Actions>
            </Modal>
        </>
    );
}

function ScanLineWithPointsMeasurementsOverview(props: {
    measurements: MeasurementView[];
    nominalValues: number[];
    isEditMode: boolean;
    showResidualLife: boolean;
    constructionYear?: number | undefined;
    project: { id: string; name: string; isScanLineOnXAxis: boolean; scanLineLabel?: string | undefined; positionLabel?: string | undefined };
    onValueChange(
        scanLineNumber: number,
        positionNumber: number,
        pointNumber: number,
        isDeleteGraph: boolean,
        value?: number,
        id?: string,
    ): Promise<void>;
}) {
    if (props.measurements.length === 0) {
        return null;
    }
    const t = useTranslate();
    const [editingValueId, setEditingValueId] = React.useState<undefined | { columnIndex: number; positionIndex: number; rowIndex: number }>(
        undefined,
    );
    const [showChartMeasurement, setShowChartMeasurement] = React.useState<
        undefined | { columnIndex?: number | undefined; positionIndex: number; rowIndex?: number | undefined }
    >(undefined);
    const maxScanlineNumber = Math.max(...props.measurements.map((m) => m.scanLineNumber));
    const maxPositionNumber = Math.max(...props.measurements.map((m) => m.positionNumber));
    const maxPointNumber = Math.max(...props.measurements.map((m) => m.pointNumber));

    const scanLineLabel = props.project.scanLineLabel ?? t('project.scanLines');
    const positionLabel = props.project.positionLabel ?? t('project.position');

    let grid: (IValueCell | undefined)[][][];
    if (props.project.isScanLineOnXAxis) {
        grid = createEmptyGridForScanLinesWithPoints(maxPositionNumber, maxScanlineNumber, maxPointNumber);
        for (const measurement of props.measurements) {
            if (grid[measurement.positionNumber - 1] && grid[measurement.positionNumber - 1]![measurement.pointNumber - 1]) {
                grid[measurement.positionNumber - 1]![measurement.pointNumber - 1]![measurement.scanLineNumber - 1] = {
                    id: measurement.id,
                    value: measurement.value,
                    createdOnDateTime: measurement.createdOnDateTime,
                    hasChartData: measurement.hasChartData,
                };
            }
        }
        grid.reverse();
    } else {
        grid = createEmptyGridForScanLinesWithPoints(maxScanlineNumber, maxPositionNumber, maxPointNumber);
        for (const measurement of props.measurements) {
            if (grid[measurement.scanLineNumber - 1] && grid[measurement.scanLineNumber - 1]![measurement.pointNumber - 1]) {
                grid[measurement.scanLineNumber - 1]![measurement.pointNumber - 1]![measurement.positionNumber - 1] = {
                    id: measurement.id,
                    value: measurement.value,
                    createdOnDateTime: measurement.createdOnDateTime,
                    hasChartData: measurement.hasChartData,
                };
            }
        }
    }

    async function handleValueChanged(
        columnIndex: number,
        rowIndex: number,
        pointIndex: number,
        isDeleteGraph: boolean,
        value?: number,
        id?: string,
    ) {
        const pointNumber = pointIndex + 1;
        const scanLineNumber = props.project.isScanLineOnXAxis ? columnIndex + 1 : rowIndex + 1;
        const positionNumber = props.project.isScanLineOnXAxis ? grid.length - rowIndex : columnIndex + 1;

        await props.onValueChange(scanLineNumber, positionNumber, pointNumber, isDeleteGraph, value, id);
        setEditingValueId(undefined);
    }

    if (grid[0] === undefined || grid[0][0] === undefined) {
        return null;
    }

    return (
        <LimitTableWidth rows={grid[0][0].length}>
            <Table whiteBackground>
                <TableHead>
                    <TableHeadRow>
                        <th> </th>
                        <th> </th>
                        <th colSpan={grid[0][0].length}>{props.project.isScanLineOnXAxis ? scanLineLabel : positionLabel}</th>
                    </TableHeadRow>
                    <TableRow>
                        <th>{props.project.isScanLineOnXAxis ? positionLabel : scanLineLabel}</th>
                        <th> </th>
                        {grid[0][0].map((_i, index) => (
                            <th key={index}>{index + 1}</th>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {grid.map((position, positionIndex) => (
                        <React.Fragment key={positionIndex}>
                            {position.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {rowIndex === 0 && (
                                        <td rowSpan={position.length} style={{ borderTopColor: '#bde2ed' }}>
                                            {props.project.isScanLineOnXAxis ? grid.length - positionIndex : positionIndex + 1}
                                            {props.project.isScanLineOnXAxis &&
                                                DisplayNominalValue(props.nominalValues, grid.length - positionIndex - 1)}
                                        </td>
                                    )}
                                    <td style={rowIndex === 0 ? { borderTopColor: '#bde2ed' } : {}}>{rowIndex + 1}</td>
                                    {row.map((cell, columnIndex) => (
                                        <td className="fs12" key={columnIndex} style={rowIndex === 0 ? { borderTopColor: '#bde2ed' } : {}}>
                                            {editingValueId &&
                                                editingValueId.columnIndex === columnIndex &&
                                                editingValueId.positionIndex === positionIndex &&
                                                editingValueId.rowIndex === rowIndex && (
                                                    <EditValue
                                                        cell={cell}
                                                        onChange={async (isDeleteGraph, value) =>
                                                            handleValueChanged(
                                                                columnIndex,
                                                                positionIndex,
                                                                rowIndex,
                                                                isDeleteGraph,
                                                                value,
                                                                cell?.id ? cell.id : undefined,
                                                            )
                                                        }
                                                        onCancel={() => setEditingValueId(undefined)}
                                                        initialValue={
                                                            cell === undefined
                                                                ? undefined
                                                                : UnitOfLength.Mm === UnitOfLength.Mm
                                                                  ? cell.value.toFixed(2)
                                                                  : (cell.value / 25.4).toFixed(3)
                                                        }
                                                    />
                                                )}
                                            <span
                                                onClick={(e: React.SyntheticEvent) => {
                                                    if (props.isEditMode) {
                                                        setEditingValueId({ columnIndex, positionIndex, rowIndex });
                                                    }
                                                    e.stopPropagation();
                                                }}
                                            >
                                                <DisplayValue
                                                    cell={cell}
                                                    unitOfLength={UnitOfLength.Mm}
                                                    nominalValues={props.project.isScanLineOnXAxis ? props.nominalValues : []}
                                                    position={props.project.isScanLineOnXAxis ? grid.length - positionIndex - 1 : positionIndex}
                                                    showResidualLife={props.showResidualLife}
                                                    constructionYear={props.constructionYear}
                                                />
                                            </span>

                                            {cell?.hasChartData && (
                                                <>
                                                    <i
                                                        className="ml5 fa fa-bar-chart-o pointer"
                                                        onClick={(e: React.SyntheticEvent) => {
                                                            if (
                                                                showChartMeasurement?.columnIndex === columnIndex &&
                                                                showChartMeasurement?.rowIndex === rowIndex
                                                            ) {
                                                                setShowChartMeasurement({
                                                                    columnIndex: undefined,
                                                                    positionIndex,
                                                                    rowIndex: undefined,
                                                                });
                                                            } else {
                                                                setShowChartMeasurement({
                                                                    columnIndex,
                                                                    positionIndex,
                                                                    rowIndex,
                                                                });
                                                            }
                                                            e.stopPropagation();
                                                        }}
                                                    />
                                                    {showChartMeasurement?.columnIndex === columnIndex &&
                                                        showChartMeasurement?.positionIndex === positionIndex &&
                                                        showChartMeasurement?.rowIndex === rowIndex && (
                                                            <ChartModal
                                                                modalId={`${columnIndex}-${rowIndex}`}
                                                                cell={cell}
                                                                handleLengthChanged={(value) =>
                                                                    void handleValueChanged(
                                                                        columnIndex,
                                                                        positionIndex,
                                                                        rowIndex,
                                                                        false,
                                                                        value,
                                                                        cell?.id ? cell.id : undefined,
                                                                    )
                                                                }
                                                                nominalValues={props.project.isScanLineOnXAxis ? props.nominalValues : []}
                                                                position={props.project.isScanLineOnXAxis ? grid.length - rowIndex - 1 : rowIndex}
                                                                showResidualLife={props.showResidualLife}
                                                                constructionYear={props.constructionYear}
                                                                project={props.project}
                                                                isEditMode={props.isEditMode}
                                                                onClose={() =>
                                                                    setShowChartMeasurement({
                                                                        columnIndex: undefined,
                                                                        positionIndex,
                                                                        rowIndex: undefined,
                                                                    })
                                                                }
                                                            />
                                                        )}
                                                </>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </LimitTableWidth>
    );
}

interface IProjectMeasurementsState {
    measurements: MeasurementView[];
    nominalPositionWidthValues: number[];
    showResidualLife: boolean;
    isLoaded: boolean;
}

interface IProjectMeasurementsProps extends ITranslateProps {
    project: {
        id: string;
        useScanLines: boolean;
        maxPositionMeasurements?: undefined | number | null;
        name: string;
        description: string;
        maxScanLinePositions?: undefined | number;
        maxScanLines?: undefined | number;
        isScanLineOnXAxis: boolean;
        isValidated: boolean;
        deviceName?: undefined | string;
        pilotName?: undefined | string;
        surveyorName?: undefined | string;
    };
    isEditMode?: undefined | boolean;
    constructionYear?: undefined | number;
}

class ProjectMeasurementsComponent extends Component<IProjectMeasurementsProps, IProjectMeasurementsState> {
    constructor(props: IProjectMeasurementsProps) {
        super(props);

        this.state = {
            measurements: [],
            nominalPositionWidthValues: [],
            showResidualLife: false,
            isLoaded: false,
        };
    }

    public override async componentDidMount() {
        await this.loadNominal();
        await this.load();
    }

    public override render() {
        if (!this.state.isLoaded) {
            return <Spinner />;
        }

        if (this.state.measurements.length === 0) {
            return <div>{this.props.t('project.noMeasurements')}</div>;
        }

        return (
            <div>
                <strong className="text-primary mt10">{this.props.project.name}</strong>
                {this.props.isEditMode && (
                    <p className="fs12">
                        {this.props.t('licence.deviceName')}: <i>{this.props.project.deviceName}</i>
                        <br />
                        {this.props.t('flightLog.droneOperator')}: <i>{this.props.project.pilotName}</i>
                        <br />
                        {this.props.t('flightLog.ndtInspector')}: <i>{this.props.project.surveyorName}</i>
                        <br />
                    </p>
                )}
                <p>{htmlHelper.newLineToBreak(this.props.project.description)}</p>
                {!this.props.project.isValidated && <div className="label-warning p5 mb5">{this.props.t('project.isValidatedMessage')}</div>}
                {!this.props.isEditMode ? (
                    <div className="clearfix pv10 mr20">
                        {this.state.nominalPositionWidthValues &&
                            this.props.constructionYear &&
                            this.props.project.useScanLines &&
                            this.props.project.isScanLineOnXAxis && (
                                <PanelButtonLink
                                    onClick={async () => this.setState({ showResidualLife: !this.state.showResidualLife })}
                                    text={this.props.t('survey.residualLife')}
                                    rightAllign={true}
                                    icon="fa fa-calendar"
                                    primary={this.state.showResidualLife}
                                    small={true}
                                />
                            )}
                    </div>
                ) : (
                    <div className="clearfix pv10">
                        <PanelLink
                            href={`/api/utprojects/${this.props.project.id}/export?scanLineAxis=x`}
                            text="Export scan line x-axis"
                            icon="fa fa-download"
                            small={true}
                            rightAllign={true}
                        />
                        <PanelLink
                            href={`/api/utprojects/${this.props.project.id}/export?scanLineAxis=y`}
                            text="Export scan line on y-axis"
                            icon="fa fa-download"
                            small={true}
                            rightAllign={true}
                        />
                    </div>
                )}
                {(this.props.project.isValidated || this.props.isEditMode) && (
                    <div>
                        {!this.props.project.useScanLines ? (
                            <FreeMeasurementsOverview
                                measurements={this.state.measurements}
                                project={this.props.project}
                                name={this.props.project.name}
                                onValueChange={this.handleValueChanged}
                                isEditMode={!!this.props.isEditMode}
                            />
                        ) : (this.props.project.maxPositionMeasurements || 0) > 1 ? (
                            <ScanLineWithPointsMeasurementsOverview
                                measurements={this.state.measurements}
                                project={this.props.project}
                                onValueChange={this.handleValueChanged}
                                isEditMode={!!this.props.isEditMode}
                                nominalValues={this.state.nominalPositionWidthValues}
                                showResidualLife={this.state.showResidualLife}
                                constructionYear={this.props.constructionYear}
                            />
                        ) : (
                            <ScanLineMeasurementsOverview
                                measurements={this.state.measurements}
                                onValueChange={this.handleValueChanged}
                                isEditMode={!!this.props.isEditMode}
                                project={this.props.project}
                                nominalValues={this.state.nominalPositionWidthValues}
                                showResidualLife={this.state.showResidualLife}
                                constructionYear={this.props.constructionYear}
                            />
                        )}
                    </div>
                )}
                {this.state.showResidualLife && <p className="fw600">{this.props.t('survey.residualLifeDisclaimer')}</p>}
            </div>
        );
    }

    private readonly handleValueChanged = async (
        scanLineNumber: number,
        positionNumber: number,
        pointNumber: number,
        isDeleteGraph: boolean,
        value?: number,
        measurementId?: string,
    ) => {
        if (value === undefined) {
            return;
        }

        await fetchPost(
            `/api/utprojects/${this.props.project.id}/measurements`,
            {},
            { indexNumber: 1, positionNumber, scanLineNumber, pointNumber, isDeleteGraph, value, measurementId },
        );

        const { measurements } = this.state;
        const measurement = measurements.find((m) => m.id === measurementId);
        if (measurement) {
            measurement.value = value;
            await this.setState({ measurements });
        }
    };

    private async load() {
        const response = await fetchGet(`/api/utprojects/${this.props.project.id}/measurements`);
        await this.setState({ measurements: response.json.items, isLoaded: true });
    }

    private async loadNominal() {
        const response = await fetchGet(`/api/utprojects/${this.props.project.id}`);
        const valueArray = response.json.nominalPositionWidthValues;
        await this.setState({ nominalPositionWidthValues: valueArray });
    }
}

export const ProjectMeasurements = translate()(ProjectMeasurementsComponent);
